import { formatInTimeZone } from 'date-fns-tz'
import type { DiscountRuleType, DiscountSchedule, DiscountScheduleInput } from '@/lib/api/discount-types'
import type { Event } from '@/lib/api/types'
import { DAYS, wallClockToInstant } from './event-clock'
import { formatMenuPrice, menuUnits, type MenuPriceOptions } from './money'

/**
 * Pure helpers behind the schedules list: status, rule, window and target
 * summaries. No React here, so the logic can be exercised on its own.
 *
 * Every wall-clock reading happens in the EVENT timezone, mirroring the
 * backend's window expansion (`vendor/pricing.py` `schedule_windows`):
 * windows are half-open [start, end); a recurring window belongs to the
 * event-local day it STARTS on (days_of_week and start_date/end_date are
 * checked against that day); an end at or before the start runs past
 * midnight into the next day. Given the event, status also follows what
 * is charged: only inside the price book's coverage, and nothing while the
 * event's promotions are off.
 */

export type PricingEvent = Pick<Event, 'event_type' | 'token_value' | 'currency'>

// ---------------------------------------------------------------------------
// Amounts

function trimNumber(value: number): string {
  return String(Number(value.toFixed(2)))
}

/**
 * A menu-unit amount: currency (units × the event's token value) on a Direct
 * Pay event, tokens on any other event. See `./money`.
 */
export function formatMenuAmount(value: number, event: PricingEvent, options?: MenuPriceOptions): string {
  return formatMenuPrice(value, menuUnits(event), options)
}

/**
 * "4 tokens" / "$4.00" (set price), "2 tokens off" / "$2.00 off", "15% off"
 * (a fraction on the wire). A set price's currency is rounded down to the
 * cent, as the backend charges it.
 */
export function formatRule(type: DiscountRuleType, value: string | number | null, event: PricingEvent): string {
  const amount = value === null || value === '' ? NaN : Number(value)
  if (!Number.isFinite(amount)) return 'No amount'
  switch (type) {
    case 'percent_off':
      return `${trimNumber(amount * 100)}% off`
    case 'tokens_off':
      return `${formatMenuAmount(amount, event)} off`
    case 'set_price':
      return formatMenuAmount(amount, event, { floor: true })
  }
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The schedule's default rule, plus how many items carry their own. */
export function ruleSummary(schedule: DiscountSchedule, event: PricingEvent): { main: string; note: string | null } {
  const overrides = schedule.items.filter((row) => row.mode === 'override').length
  const notes: string[] = []
  if (overrides) notes.push(`${plural(overrides, 'item')} with ${overrides === 1 ? 'its' : 'their'} own price`)
  // Saved before the event became a token event: the backend leaves such prices alone.
  const percentOnTokens = event.event_type === 'token' && (
    schedule.default_rule_type === 'percent_off' || schedule.items.some((row) => row.mode === 'override' && row.rule_type === 'percent_off'))
  if (percentOnTokens) notes.push('percent off does not apply on a token event')
  return {
    main: schedule.default_rule_type
      ? formatRule(schedule.default_rule_type, schedule.default_rule_value, event)
      : 'Per-item prices',
    note: notes.length ? notes.join('; ') : null,
  }
}

/** "2 vendors · 1 location · 3 items (1 excluded)". Excluded items count toward the item total. */
export function targetsSummary(schedule: Pick<DiscountSchedule, 'vendors' | 'locations' | 'items'>): string {
  const parts: string[] = []
  if (schedule.vendors.length) parts.push(plural(schedule.vendors.length, 'vendor'))
  if (schedule.locations.length) parts.push(plural(schedule.locations.length, 'location'))
  if (schedule.items.length) {
    const excluded = schedule.items.filter((row) => row.mode === 'exclude').length
    parts.push(`${plural(schedule.items.length, 'item')}${excluded ? ` (${excluded} excluded)` : ''}`)
  }
  return parts.length ? parts.join(' · ') : 'Nothing targeted'
}

// ---------------------------------------------------------------------------
// Event-local calendar arithmetic. Dates are "YYYY-MM-DD" strings in the
// event timezone; they compare correctly as strings.

// date-fns's `MMM`, so plain dates read like the formatted instants beside them.
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function parseYmd(ymd: string): [number, number, number] {
  const [y, m, d] = ymd.split('-').map(Number)
  return [y, m, d]
}

function addDays(ymd: string, days: number): string {
  const [y, m, d] = parseYmd(ymd)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/** 0 = Monday … 6 = Sunday, like the backend's `date.weekday()`. */
function weekday(ymd: string): number {
  const [y, m, d] = parseYmd(ymd)
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
}

/** "16:00" or "16:00:00(.ffffff)" → "16:00:00". */
function normalizeClock(clock: string): string {
  const [h = '0', m = '0', s = '0'] = clock.split(':')
  const pad = (part: string) => String(Math.trunc(Number(part)) || 0).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

/** Event-local date + wall clock → instant, DST resolved as the backend's `localize` does. */
function localize(ymd: string, clock: string, timeZone: string): Date {
  return wallClockToInstant(ymd, clock, timeZone)
}

function localDate(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'yyyy-MM-dd')
}

// ---------------------------------------------------------------------------
// Status

export type ScheduleStatus = 'archived' | 'paused' | 'running' | 'not_charging' | 'scheduled' | 'ended'

export const STATUS_LABEL: Record<ScheduleStatus, string> = {
  archived: 'Archived',
  paused: 'Paused',
  running: 'Running now',
  not_charging: 'Not charging',
  scheduled: 'Scheduled',
  ended: 'Ended',
}

export type StatusInfo =
  | { status: 'archived' | 'paused' }
  /** `until`: end of the open window; null = no end set, or none within a month. */
  | { status: 'running'; until: Date | null }
  /** A window is open, but the event's promotions are off, so nothing is discounted. */
  | { status: 'not_charging' }
  /** `next`: start of the next window. */
  | { status: 'scheduled'; next: Date }
  /** `outsideEvent`: windows are left, but none inside the dates the event's prices cover. */
  | { status: 'ended'; outsideEvent?: boolean }

/** The event fields that decide what the price books cover. */
export type EventDates = Pick<Event, 'start_date_time' | 'end_date_time'>

/** The event fields that decide whether an open window is charged. */
export type StatusEvent = EventDates & Pick<Event, 'enable_discount_schedules'>

/** A schedule's window fields: a saved schedule's, or a draft's body. */
export type WindowFields = Pick<
  DiscountScheduleInput,
  'window_type' | 'starts_at' | 'ends_at' | 'days_of_week' | 'daily_start_time' | 'daily_end_time' | 'start_date' | 'end_date'
>

interface Window { start: Date; end: Date }

interface Coverage extends Window {
  /** The event's start and end dates, event-local (the end never before the start). */
  startDate: string
  endDate: string
  /** Event-local days whose windows can fall inside it: the day before the start date … the day after the end date. */
  firstDay: string
  lastDay: string
}

const MIN_DATE = new Date(-8.64e15)
const MAX_DATE = new Date(8.64e15)

/**
 * What the event's price books cover (`build_price_book`, with the span from
 * `price_book._span_for_event`): event-local midnight of the start date up
 * to midnight two days after the end date. Nothing outside it is charged.
 * null when the event has no dates.
 */
function bookCoverage(event: EventDates, timeZone: string): Coverage | null {
  const eventStart = toDate(event.start_date_time ?? null)
  const eventEnd = toDate(event.end_date_time ?? null)
  if (!eventStart || !eventEnd) return null
  const startDay = localDate(eventStart, timeZone)
  const endDay = localDate(eventEnd, timeZone)
  // `coverage_span` treats an end before the start as the start.
  const lastDate = endDay < startDay ? startDay : endDay
  return {
    start: localize(startDay, '00:00:00', timeZone),
    end: localize(addDays(lastDate, 2), '00:00:00', timeZone),
    startDate: startDay,
    endDate: lastDate,
    firstDay: addDays(startDay, -1),
    lastDay: addDays(lastDate, 1),
  }
}

function clip(window: Window, coverage: Coverage | null): Window | null {
  const start = coverage && coverage.start > window.start ? coverage.start : window.start
  const end = coverage && coverage.end < window.end ? coverage.end : window.end
  return start < end ? { start, end } : null
}

function recurringWindowOn(schedule: WindowFields, day: string, timeZone: string): Window | null {
  if (!schedule.daily_start_time || !schedule.daily_end_time) return null
  if (schedule.start_date && day < schedule.start_date) return null
  if (schedule.end_date && day > schedule.end_date) return null
  const days = schedule.days_of_week ?? []
  if (days.length && !days.includes(weekday(day))) return null
  const startClock = normalizeClock(schedule.daily_start_time)
  const endClock = normalizeClock(schedule.daily_end_time)
  const crosses = endClock <= startClock
  const start = localize(day, startClock, timeZone)
  const end = localize(crosses ? addDays(day, 1) : day, endClock, timeZone)
  // A same-day window wholly inside a spring-forward gap collapses; the backend drops it too.
  return start < end ? { start, end } : null
}

// How far "until" follows back-to-back windows (a 24-hour daily window).
const MAX_CHAINED_DAYS = 31

// Every weekday comes round twice in two weeks, so a scan this long meets
// each chosen day at least once where no DST gap swallows its window.
const FORTNIGHT = 14

/** Whether a window is still open or to come at `now`, event dates aside. */
function recurringWindowLeft(schedule: WindowFields, timeZone: string, now: Date): boolean {
  let day = addDays(localDate(now, timeZone), -1)
  if (schedule.start_date && schedule.start_date > day) day = schedule.start_date
  for (let i = 0; i <= FORTNIGHT; i++, day = addDays(day, 1)) {
    if (schedule.end_date && day > schedule.end_date) return false
    const window = recurringWindowOn(schedule, day, timeZone)
    if (window && window.end > now) return true
  }
  return false
}

function recurringStatus(schedule: DiscountSchedule, timeZone: string, now: Date, coverage: Coverage | null): StatusInfo {
  const windowOn = (day: string) => {
    const window = recurringWindowOn(schedule, day, timeZone)
    return window && clip(window, coverage)
  }
  const today = localDate(now, timeZone)
  // A window that started yesterday may still be open (it crosses midnight).
  for (const day of [addDays(today, -1), today]) {
    const window = windowOn(day)
    if (window && window.start <= now && now < window.end) {
      let until = window.end
      let next = day
      let chained = 0
      for (; chained < MAX_CHAINED_DAYS; chained++) {
        next = addDays(next, 1)
        const following = windowOn(next)
        if (!following || following.start.getTime() !== until.getTime()) break
        until = following.end
      }
      // Still back to back after a month: no end in sight.
      return { status: 'running', until: chained === MAX_CHAINED_DAYS ? null : until }
    }
  }
  // Every weekday comes round within a week of the first eligible day, plus
  // one day for the day before the coverage, whose window the clip may empty.
  let day = today
  if (schedule.start_date && schedule.start_date > day) day = schedule.start_date
  if (coverage && coverage.firstDay > day) day = coverage.firstDay
  for (let i = 0; i < 9; i++, day = addDays(day, 1)) {
    if (schedule.end_date && day > schedule.end_date) break
    if (coverage && day > coverage.lastDay) return { status: 'ended', outsideEvent: true }
    const window = windowOn(day)
    if (window && window.start > now) return { status: 'scheduled', next: window.start }
  }
  // Windows are left, but none inside the coverage (the last date comes before it).
  if (coverage && recurringWindowLeft(schedule, timeZone, now)) return { status: 'ended', outsideEvent: true }
  return { status: 'ended' }
}

function oneOffStatus(schedule: DiscountSchedule, now: Date, coverage: Coverage | null): StatusInfo {
  const start = toDate(schedule.starts_at)
  const end = toDate(schedule.ends_at)
  if (end && (end <= now || (start && end <= start))) return { status: 'ended' }
  const window = clip({ start: start ?? MIN_DATE, end: end ?? MAX_DATE }, coverage)
  if (!window || window.end <= now) return { status: 'ended', outsideEvent: true }
  if (window.start > now) return { status: 'scheduled', next: window.start }
  // Open-ended: the coverage's edge is not an end the schedule sets.
  return { status: 'running', until: end ? window.end : null }
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Archived (archived_at) → Paused (!is_active) → Running now (a window is
 * open at `now`) → Scheduled (a window starts later) → Ended (none left).
 *
 * With `event`, the status is what the backend charges: windows count only
 * inside the price book's coverage (event-local midnight of the start date
 * to midnight two days after the end date), so before it the schedule is
 * Scheduled at the coverage's first window and after it Ended; and while the
 * event's promotions are off an open window is Not charging.
 */
export function scheduleStatus(schedule: DiscountSchedule, timeZone: string, now: Date, event?: StatusEvent): StatusInfo {
  if (schedule.archived_at) return { status: 'archived' }
  if (!schedule.is_active) return { status: 'paused' }
  const coverage = event ? bookCoverage(event, timeZone) : null
  const info = schedule.window_type === 'one_off'
    ? oneOffStatus(schedule, now, coverage)
    : recurringStatus(schedule, timeZone, now, coverage)
  if (info.status === 'running' && event?.enable_discount_schedules === false) return { status: 'not_charging' }
  return info
}

// ---------------------------------------------------------------------------
// Event dates

/**
 * - `none`: no window reaches the dates the event's prices cover, so the
 *   schedule never discounts anything.
 * - `partial`: some window, or part of one, falls outside them; only what is
 *   inside is discounted.
 * - `full`: every window falls inside them.
 */
export type EventOverlap = 'none' | 'partial' | 'full'

export interface EventDatesFit {
  overlap: EventOverlap
  /** The event's start and end dates, event-local "YYYY-MM-DD" (the end never before the start). */
  startDate: string
  endDate: string
  /**
   * A bound the user set (a one-off start or end, a recurring first or last
   * date) lies outside the event's dates. An open side doesn't count: an
   * open-ended window always runs past the event, which needs no note.
   */
  boundBeyondEvent: boolean
}

function boundBeyondEvent(schedule: WindowFields, coverage: Coverage): boolean {
  if (schedule.window_type === 'one_off') {
    const start = toDate(schedule.starts_at)
    const end = toDate(schedule.ends_at)
    return (!!start && start < coverage.start) || (!!end && end > coverage.end)
  }
  return (!!schedule.start_date && schedule.start_date < coverage.startDate)
    || (!!schedule.end_date && schedule.end_date > coverage.endDate)
}

function fit(window: Window, coverage: Coverage): EventOverlap {
  const clipped = clip(window, coverage)
  if (!clipped) return 'none'
  return clipped.start > window.start || clipped.end < window.end ? 'partial' : 'full'
}

function oneOffOverlap(schedule: WindowFields, coverage: Coverage): EventOverlap | null {
  const start = toDate(schedule.starts_at)
  const end = toDate(schedule.ends_at)
  // An end at or before the start is an error of its own.
  if (start && end && end <= start) return null
  return fit({ start: start ?? MIN_DATE, end: end ?? MAX_DATE }, coverage)
}

function recurringOverlap(schedule: WindowFields, timeZone: string, coverage: Coverage): EventOverlap | null {
  if (!schedule.daily_start_time || !schedule.daily_end_time) return null
  if (schedule.start_date && schedule.end_date && schedule.end_date < schedule.start_date) return null
  const fits = new Set<EventOverlap>()
  // Only windows starting on these days can reach the coverage.
  for (let day = coverage.firstDay; day <= coverage.lastDay; day = addDays(day, 1)) {
    const window = recurringWindowOn(schedule, day, timeZone)
    if (window) fits.add(fit(window, coverage))
  }
  if (!fits.has('partial') && !fits.has('full')) return 'none'
  if (fits.has('partial') || fits.has('none')) return 'partial'
  // Every window on a day beyond them lies outside. With no first or last
  // date the windows go on forever, and two weeks either side finds them.
  const windowOnAny = (from: string) => {
    for (let i = 0, day = from; i < FORTNIGHT; i++, day = addDays(day, 1)) {
      if (recurringWindowOn(schedule, day, timeZone)) return true
    }
    return false
  }
  const outside = windowOnAny(addDays(coverage.firstDay, -FORTNIGHT)) || windowOnAny(addDays(coverage.lastDay, 1))
  return outside ? 'partial' : 'full'
}

/**
 * How a schedule's windows (a saved schedule, or a draft's body) sit against
 * the dates the event's prices cover, judged with the same coverage and
 * windows as `scheduleStatus`: where this says `none`, it says Ended (outside
 * the event dates while any window is still to come). null when the event
 * has no dates or the window can't be judged (an end before its start, no
 * daily hours).
 */
export function eventDatesFit(schedule: WindowFields, event: EventDates, timeZone: string): EventDatesFit | null {
  const coverage = bookCoverage(event, timeZone)
  if (!coverage) return null
  const overlap = schedule.window_type === 'one_off'
    ? oneOffOverlap(schedule, coverage)
    : recurringOverlap(schedule, timeZone, coverage)
  return overlap && {
    overlap,
    startDate: coverage.startDate,
    endDate: coverage.endDate,
    boundBeyondEvent: boundBeyondEvent(schedule, coverage),
  }
}

// ---------------------------------------------------------------------------
// Wording

function parseClock(clock: string): { hours: number; minutes: number } {
  const [h, m] = normalizeClock(clock).split(':').map(Number)
  return { hours: h, minutes: m }
}

function formatClock(clock: string, withMeridiem = true): string {
  const { hours, minutes } = parseClock(clock)
  const text = `${hours % 12 || 12}:${String(minutes).padStart(2, '0')}`
  return withMeridiem ? `${text} ${hours < 12 ? 'AM' : 'PM'}` : text
}

/** "4:00–6:00 PM", "10:00 PM–2:00 AM". */
function clockRange(startClock: string, endClock: string): string {
  const start = normalizeClock(startClock)
  const end = normalizeClock(endClock)
  const sameHalf = parseClock(start).hours < 12 === parseClock(end).hours < 12
  if (start < end && sameHalf) return `${formatClock(start, false)}–${formatClock(end)}`
  return `${formatClock(start)}–${formatClock(end)}`
}

/** "Mon–Fri", "Mon, Wed, Fri", "Sat–Mon" (a run may wrap past Sunday), "Every day". */
export function daysSummary(days: number[]): string {
  const set = new Set(days.filter((d) => d >= 0 && d <= 6))
  if (set.size === 0 || set.size === 7) return 'Every day'
  // Begin at a day whose previous day is off, so a run through Sunday stays whole.
  const first = [0, 1, 2, 3, 4, 5, 6].find((d) => set.has(d) && !set.has((d + 6) % 7)) ?? 0
  const runs: number[][] = []
  let run: number[] = []
  for (let i = 0; i < 7; i++) {
    const d = (first + i) % 7
    if (set.has(d)) {
      run.push(d)
    } else if (run.length) {
      runs.push(run)
      run = []
    }
  }
  if (run.length) runs.push(run)
  return runs
    .map((r) => (r.length >= 3 ? `${DAYS[r[0]].short}–${DAYS[r[r.length - 1]].short}` : r.map((d) => DAYS[d].short).join(', ')))
    .join(', ')
}

/** "Oct 1", or "Oct 1, 2027" outside the current year. */
function formatYmd(ymd: string, currentYear: string): string {
  const [y, m, d] = parseYmd(ymd)
  return `${MONTH_NAMES[m - 1]} ${d}${String(y) === currentYear ? '' : `, ${y}`}`
}

/** "Oct 3 – Oct 5", or "Oct 3" for a single day; event-local "YYYY-MM-DD" dates. */
export function formatDateSpan(first: string, last: string, timeZone: string, now: Date): string {
  const currentYear = formatInTimeZone(now, timeZone, 'yyyy')
  if (last <= first) return formatYmd(first, currentYear)
  return `${formatYmd(first, currentYear)} – ${formatYmd(last, currentYear)}`
}

/** "Sat, Oct 12", or "Sat, Oct 12, 2027" outside the current year. */
function formatInstantDay(instant: Date, timeZone: string, currentYear: string): string {
  const sameYear = formatInTimeZone(instant, timeZone, 'yyyy') === currentYear
  return formatInTimeZone(instant, timeZone, sameYear ? 'EEE, MMM d' : 'EEE, MMM d, yyyy')
}

function instantClock(instant: Date, timeZone: string): string {
  return formatInTimeZone(instant, timeZone, 'HH:mm')
}

/** "Sat, Oct 12, 4:00 PM". */
function formatInstant(instant: Date, timeZone: string, currentYear: string): string {
  return `${formatInstantDay(instant, timeZone, currentYear)}, ${formatClock(instantClock(instant, timeZone))}`
}

/**
 * "Sat, Oct 12, 4:00–6:00 PM", "Mon–Fri 4:00–6:00 PM, Oct 1 – Oct 31",
 * "Every day 10:00 PM–2:00 AM". Wall clock in the event timezone.
 */
export function windowSummary(schedule: DiscountSchedule, timeZone: string, now: Date): string {
  const currentYear = formatInTimeZone(now, timeZone, 'yyyy')
  if (schedule.window_type === 'one_off') {
    const start = toDate(schedule.starts_at)
    const end = toDate(schedule.ends_at)
    if (start && end) {
      if (localDate(start, timeZone) === localDate(end, timeZone)) {
        return `${formatInstantDay(start, timeZone, currentYear)}, ${clockRange(instantClock(start, timeZone), instantClock(end, timeZone))}`
      }
      return `${formatInstant(start, timeZone, currentYear)} – ${formatInstant(end, timeZone, currentYear)}`
    }
    if (start) return `From ${formatInstant(start, timeZone, currentYear)}`
    if (end) return `Until ${formatInstant(end, timeZone, currentYear)}`
    return 'Always (no start or end)'
  }

  const days = daysSummary(schedule.days_of_week ?? [])
  let when = `${days}, no daily hours set`
  if (schedule.daily_start_time && schedule.daily_end_time) {
    const start = normalizeClock(schedule.daily_start_time)
    const end = normalizeClock(schedule.daily_end_time)
    if (start !== end) when = `${days} ${clockRange(start, end)}`
    else if (start === '00:00:00') when = `${days}, all day`
    else when = `${days} ${formatClock(start)} for 24 hours`
  }
  const parts = [when]
  const { start_date: from, end_date: to } = schedule
  if (from && to) parts.push(`${formatYmd(from, currentYear)} – ${formatYmd(to, currentYear)}`)
  else if (from) parts.push(`from ${formatYmd(from, currentYear)}`)
  else if (to) parts.push(`until ${formatYmd(to, currentYear)}`)
  return parts.join(', ')
}

/**
 * The line under the status pill: "until 6:00 PM today", "starts tomorrow,
 * 4:00 PM", "ended Sat, Oct 12", "archived Fri, Oct 3", "promotions are off
 * for this event".
 */
export function statusDetail(info: StatusInfo, schedule: DiscountSchedule, timeZone: string, now: Date): string {
  const currentYear = formatInTimeZone(now, timeZone, 'yyyy')
  const today = localDate(now, timeZone)
  const when = (instant: Date) => {
    const day = localDate(instant, timeZone)
    const clock = formatClock(instantClock(instant, timeZone))
    if (day === today) return `${clock} today`
    if (day === addDays(today, 1)) return `tomorrow, ${clock}`
    return formatInstant(instant, timeZone, currentYear)
  }
  switch (info.status) {
    case 'running':
      return info.until ? `until ${when(info.until)}` : 'no end set'
    case 'not_charging':
      return 'promotions are off for this event'
    case 'scheduled':
      return `starts ${when(info.next)}`
    case 'ended': {
      if (info.outsideEvent) return 'outside the event dates'
      const end = schedule.window_type === 'one_off' ? toDate(schedule.ends_at) : null
      if (end) return `ended ${formatInstantDay(end, timeZone, currentYear)}`
      if (schedule.window_type === 'recurring' && schedule.end_date) return `last day ${formatYmd(schedule.end_date, currentYear)}`
      return 'no window left'
    }
    case 'archived': {
      const archived = toDate(schedule.archived_at)
      return archived ? `archived ${formatInstantDay(archived, timeZone, currentYear)}` : ''
    }
    case 'paused':
      return ''
  }
}
